import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  startLoadingSearchKeyTerm,
  startLoadingSearchTermFields,
  startSavingSearchKeyTerm,
  startSettingSearchKeyTermStatus
} from "../../app/actions/searchKeyTermActions";
import { FLOW_MODULE_DEFINITIONS, getModuleDisplayLabel } from "../../app/helpers/flowEngineConfigHelper";
import { toFeederFlowRecord } from "../../app/helpers/feederFlowRecordHelper";
import useActiveSpecialistRole from "../../app/hooks/useActiveSpecialistRole";
import NavbarIconsModuleContext from "../generic-nav-bar/NavbarIconsModuleContext";
import useFeederTrayRoleSync from "./useFeederTrayRoleSync";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import FlowEngineSection from "../specialist-flow-engine/FlowEngineSection";
import SearchTermTypeahead from "./SearchTermTypeahead";

const MODULES = Object.entries(FLOW_MODULE_DEFINITIONS).map(([key, def]) => ({
  key,
  id: def.moduleId
}));

const emptyForm = {
  catalogmodule_id: "",
  title: "",
  brief: "",
  open_text: ""
};

const SpecialistFeederTermScreen = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const keepRole = Boolean(location.state?.keepRole);
  const returnTo = location.state?.returnTo || "/specialist-feeder";
  const { t } = useTranslation();
  const { fields, openTextEnabled, titleEnabled, briefEnabled, saving, selected } = useSelector((state) => state.searchKeyTerm);
  const { roleKey, selectedRole } = useActiveSpecialistRole();
  useFeederTrayRoleSync({ enabled: !keepRole });
  const [form, setForm] = useState(emptyForm);
  const [combos, setCombos] = useState({});
  const [termStatus, setTermStatus] = useState("");

  useEffect(() => {
    if (!id) {
      return;
    }
    dispatch(startLoadingSearchKeyTerm(id)).then((body) => {
      if (!body?.row) {
        return;
      }
      const row = body.row;
      setForm({
        catalogmodule_id: String(row.catalogmodule_id || ""),
        title: row.title || "",
        brief: row.brief || "",
        open_text: row.open_text || ""
      });
      const next = {};
      (body.labels ? Object.keys(body.labels) : []).forEach((key) => {
        if (row[key]) {
          next[key] = { id: row[key], label: body.labels[key], level: row.ciiu_level };
        }
      });
      setCombos(next);
      setTermStatus(row.status || "");
    });
  }, [dispatch, id]);

  const confirmFinished = () => {
    Swal.fire({
      title: t("specialist.feeder.term.finish-title", "Marcar como Terminado"),
      text: t(
        "specialist.feeder.term.finish-text",
        "En el momento en que pongas este término clave en Terminado ya no se podrán relacionar más términos clave de búsqueda con DRI usando este registro."
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("specialist.feeder.term.finish-confirm", "Pasar a Terminado"),
      cancelButtonText: t("buttons.cancel", "Cancelar")
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }
      const saved = await dispatch(startSettingSearchKeyTermStatus(id, "finished", selectedRole?.name));
      if (saved) {
        setTermStatus("finished");
      }
    });
  };

  const reopenAssigned = async () => {
    const saved = await dispatch(startSettingSearchKeyTermStatus(id, "assigned", selectedRole?.name));
    if (saved) {
      setTermStatus("assigned");
    }
  };

  const canFinish = (roleKey === "coordinator" || roleKey === "pre-manager") && termStatus === "assigned";
  const canReopen = roleKey === "coordinator" && termStatus === "finished";

  useEffect(() => {
    if (!form.catalogmodule_id) {
      return;
    }
    dispatch(startLoadingSearchTermFields(form.catalogmodule_id));
  }, [dispatch, form.catalogmodule_id]);

  const flowRecord = useMemo(() => {
    if (!id || !selected || (roleKey !== "feeder" && !keepRole)) {
      return null;
    }
    return toFeederFlowRecord(selected, selectedRole?.id);
  }, [id, keepRole, roleKey, selected, selectedRole]);

  const pairs = useMemo(() => {
    const list = fields || [];
    const chunks = [];
    for (let index = 0; index < list.length; index += 2) {
      chunks.push(list.slice(index, index + 2));
    }
    return chunks;
  }, [fields]);

  const setCombo = (fieldKey, option) => {
    setCombos((prev) => {
      const next = { ...prev, [fieldKey]: option };
      if (fieldKey === "catalogtopic_id") {
        next.catalogquestion_id = null;
      }
      return next;
    });
  };

  const handleModule = (catalogmoduleId) => {
    setForm((prev) => ({ ...prev, catalogmodule_id: catalogmoduleId }));
    setCombos({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.catalogmodule_id || (titleEnabled && !form.title.trim())) {
      Swal.fire(
        t("titles.error"),
        titleEnabled
          ? t("specialist.feeder.term.required-base", "El módulo y el título son obligatorios.")
          : t("specialist.feeder.term.required-module", "El módulo es obligatorio."),
        "error"
      );
      return;
    }
    const missing = (fields || []).filter((field) => field.required && !combos[field.field_key]?.id);
    if (missing.length) {
      Swal.fire(
        t("titles.error"),
        `${t("specialist.feeder.term.required-combos", "Faltan campos obligatorios")}: ${missing.map((field) => field.label).join(", ")}`,
        "error"
      );
      return;
    }
    const payload = {
      catalogmodule_id: Number(form.catalogmodule_id),
      title: titleEnabled ? form.title.trim() : "",
      brief: briefEnabled ? form.brief : null,
      open_text: openTextEnabled ? form.open_text : null
    };
    (fields || []).forEach((field) => {
      const picked = combos[field.field_key];
      payload[field.field_key] = picked?.id || null;
      if (field.field_key === "catalogciiu_id") {
        payload.ciiu_level = picked?.level ?? null;
      }
    });
    const saved = await dispatch(startSavingSearchKeyTerm(payload, id));
    if (saved) {
      navigate("/specialist-feeder");
    }
  };

  return (
    <FlowEngineGlobalGate>
      <NavbarIconsModuleContext
        moduleProcess="feeder"
        mode="ficha"
        returnTo={returnTo}
        returnLabelKey="specialist.feeder.tray.title"
        returnLabel={t("specialist.feeder.tray.title", "Términos clave de búsqueda")}
      />
      <div className="container py-4">
        <h3 className="mb-1">
          {id
            ? t("specialist.feeder.term.edit-title", "Término clave")
            : t("specialist.feeder.term.new-title", "Nuevo término clave")}
        </h3>
        <p className="text-muted">
          {t(
            "specialist.feeder.term.subtitle",
            "Los combos cambian según el módulo. El término de búsqueda queda armado con el combo obligatorio y los opcionales que completes."
          )}
        </p>
        {(canFinish || canReopen || termStatus === "finished" || termStatus === "assigned") && (roleKey === "coordinator" || roleKey === "pre-manager") && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className={`badge bg-${termStatus === "finished" ? "dark" : "success"}`}>
              {termStatus === "finished"
                ? t("specialist.feeder.status.finished", "Terminado")
                : t("specialist.feeder.status.assigned", "Asignado")}
            </span>
            {canFinish && (
              <button type="button" className="btn btn-outline-dark btn-sm" onClick={confirmFinished}>
                {t("specialist.feeder.term.finish-confirm", "Pasar a Terminado")}
              </button>
            )}
            {canReopen && (
              <button type="button" className="btn btn-outline-success btn-sm" onClick={reopenAssigned}>
                {t("specialist.feeder.term.reopen", "Volver a Asignado")}
              </button>
            )}
          </div>
        )}
        {flowRecord?.process && (
          <FlowEngineSection
            layoutMode="edit"
            record={flowRecord}
            moduleProcess={flowRecord.process}
            moduleName={getModuleDisplayLabel(flowRecord.process, t)}
            subjectKind="search_key_term"
            poolSurface={keepRole && roleKey && roleKey !== "feeder" ? roleKey : "feeder"}
            onAfterAction={() => dispatch(startLoadingSearchKeyTerm(id))}
          />
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="term-module">
              {t("flow.cmi.col.module", "Módulo")} *
            </label>
            <select
              id="term-module"
              className="form-select"
              value={form.catalogmodule_id}
              onChange={(event) => handleModule(event.target.value)}
            >
              <option value="">{t("specialist.feeder.term.pick-module", "Selecciona un módulo")}</option>
              {MODULES.map((item) => (
                <option key={item.id} value={item.id}>{getModuleDisplayLabel(item.key, t)}</option>
              ))}
            </select>
          </div>
          {titleEnabled && (
            <div className="mb-3">
              <label className="form-label" htmlFor="term-title">
                {t("specialist.feeder.term.title", "Título")} *
              </label>
              <input
                id="term-title"
                className="form-control"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>
          )}
          {form.catalogmodule_id && pairs.map((pair) => (
            <div className="row" key={pair.map((field) => field.id).join("-")}>
              {pair.map((field) => (
                <div className="col-md-6" key={field.id}>
                  <SearchTermTypeahead
                    fieldKey={field.field_key}
                    label={field.label}
                    required={field.required}
                    sourceKind={field.source_kind}
                    catalogtypeId={field.source_catalogtype_id}
                    topicId={combos.catalogtopic_id?.id || null}
                    disabled={field.field_key === "catalogquestion_id" && !combos.catalogtopic_id?.id}
                    value={combos[field.field_key] || null}
                    onChange={(option) => setCombo(field.field_key, option)}
                  />
                </div>
              ))}
            </div>
          ))}
          {briefEnabled && (
            <div className="mb-3">
              <label className="form-label" htmlFor="term-brief">
                {t("specialist.feeder.term.brief", "Resumen")}
              </label>
              <textarea
                id="term-brief"
                className="form-control"
                rows={4}
                value={form.brief}
                onChange={(event) => setForm((prev) => ({ ...prev, brief: event.target.value }))}
              />
            </div>
          )}
          {openTextEnabled && (
            <div className="mb-3">
              <label className="form-label" htmlFor="term-open">
                {t("specialist.feeder.term.open-text", "Texto abierto")}
              </label>
              <textarea
                id="term-open"
                className="form-control"
                rows={3}
                value={form.open_text}
                onChange={(event) => setForm((prev) => ({ ...prev, open_text: event.target.value }))}
              />
            </div>
          )}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {t("buttons.save", "Guardar")}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(returnTo)}>
              {t("buttons.back", "Volver")}
            </button>
          </div>
        </form>
      </div>
    </FlowEngineGlobalGate>
  );
};

export default SpecialistFeederTermScreen;
